import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Badge, Alert, Table, Spinner, ProgressBar } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../hooks';
import { newHireTrainingAPI } from '../../services';
import { toast } from 'react-toastify';

/**
 * #005135 — Theo dõi "tập việc" của nhân viên mới (AC4).
 *
 * #190609 — mốc đổi sang **số ca đã được duyệt** đầu tiên (không còn tính theo 30 ngày).
 * #011742 (18/09/2026) — mốc giảm còn **8 ca** (trước là 16). Con số THẬT lấy từ server
 * (`shifts_required`), đừng ghim cứng ở giao diện.
 * Nhân viên mới **chỉ nhận ca gãy** (ca phụ = ca không thu ngân), vẫn làm
 * đơn nước bình thường để luyện tay. Đủ số ca thì bot tự đề xuất lại ca chính (ca thu ngân) —
 * màn này chỉ để theo dõi, không có nút nào để bấm mở/khoá.
 */
function OrdersBadge({ done, required }) {
  const met = done >= required;
  return (
    <Badge bg={met ? 'success' : 'warning'} text={met ? 'white' : 'dark'}>
      {done}/{required} đơn
    </Badge>
  );
}

export default function NewHireTrainingPage() {
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [ordersRequired, setOrdersRequired] = useState(0);
  const [shiftsRequired, setShiftsRequired] = useState(8);

  const loadData = async () => {
    if (!selectedBranch?.id) return;
    setLoading(true);
    try {
      const res = await newHireTrainingAPI.getBranchProgress(selectedBranch.id);
      setData(Array.isArray(res?.data) ? res.data : []);
      setOrdersRequired(res?.orders_required ?? 0);
      setShiftsRequired(res?.shifts_required ?? 8);
    } catch (err) {
      toast.error(err?.message || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch?.id]);

  const notMet = data.filter((r) => !r.orders_met).length;

  return (
    <PageLayout title="Tập việc (nhân viên mới)">
      <Alert variant="info" className="mb-4">
        Nhân viên trong <b>{shiftsRequired} ca đầu tiên được duyệt</b> nên ưu tiên{' '}
        <b>ca gãy</b> (ca phụ, không thu ngân) để luyện tay. <b>Không có rào cứng nào</b>: NV vẫn
        đăng ký được ca chính (app chỉ nhắc) và quản lý vẫn duyệt/xếp bình thường — chỗ duy nhất
        bị chặn là <b>bot không tự ĐỀ XUẤT</b> ca chính cho họ, đủ {shiftsRequired} ca thì bot tự
        đề xuất lại. Chưa đủ số đơn cũng chỉ cảnh báo mềm.
      </Alert>

      <Row className="mb-4 g-3">
        <Col xs={12} md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="fs-2 fw-bold text-primary">{data.length}</div>
              <div className="text-muted small">NV chưa đủ {shiftsRequired} ca duyệt</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="fs-2 fw-bold text-warning">{notMet}</div>
              <div className="text-muted small">Chưa đủ số đơn luyện tay</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="fs-2 fw-bold text-secondary">{ordersRequired}</div>
              <div className="text-muted small">Ngưỡng đơn luyện tay (chi nhánh này)</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white d-flex align-items-center gap-3 flex-wrap py-3">
          <span className="fw-semibold">Danh sách nhân viên đang tập việc</span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="ms-auto"
          >
            {loading ? <Spinner size="sm" animation="border" /> : 'Làm mới'}
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : data.length === 0 ? (
            <Alert variant="light" className="m-3 mb-0">
              Chi nhánh này hiện không có nhân viên nào đang tập việc.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle" data-testid="new-hire-training-table">
                <thead className="table-light">
                  <tr>
                    <th>Nhân viên</th>
                    <th className="text-center">Ngày vào làm</th>
                    <th className="text-center">Ca đã duyệt</th>
                    <th style={{ minWidth: 180 }}>Đơn nước đã làm</th>
                    <th className="text-center">Ca chính</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.employee_id}>
                      <td>
                        <div className="fw-semibold">{row.employee_name}</div>
                        <div className="text-muted small">{row.employee_id.slice(0, 8)}…</div>
                      </td>
                      <td className="text-center">
                        {row.joined_at
                          ? new Date(row.joined_at).toLocaleDateString('vi-VN')
                          : '—'}
                      </td>
                      <td className="text-center">
                        <Badge bg="info" text="dark">
                          {row.shifts_approved}/{row.shifts_required} ca
                        </Badge>
                        <div className="text-muted small mt-1">
                          còn {row.shifts_left} ca
                        </div>
                      </td>
                      <td>
                        <ProgressBar
                          now={
                            row.orders_required > 0
                              ? Math.min(100, (row.orders_done / row.orders_required) * 100)
                              : 100
                          }
                          variant={row.orders_met ? 'success' : 'warning'}
                          style={{ height: 8 }}
                          className="mb-1"
                        />
                        <OrdersBadge done={row.orders_done} required={row.orders_required} />
                      </td>
                      <td className="text-center">
                        <Badge bg="secondary">🔒 Chưa nhận được</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </PageLayout>
  );
}
